import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BulkResult, MessageSummary } from '@/api';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { ObjectUrlRegistry, triggerDownload } from '@/features/message-frame/subresources';

/**
 * The two message verbs that are not list mutations: download the original,
 * and copy to another folder.
 *
 * They live next to the menu that offers them rather than in
 * `useMessageMutations`, which is the optimistic-patch engine: neither of
 * these patches a cached list. A download changes nothing at all, and a copy
 * adds a row to a folder the cache has no insert patch for — inventing one
 * would put a row on screen that the server has not confirmed and that carries
 * an id nobody can open.
 */

/** Characters no filesystem enjoys, plus the ones that would split a path. */
const UNSAFE_FILENAME = /[\\/:*?"<>|]/g;
/**
 * Control characters become spaces — the CR/LF among them is the one that
 * would split a header. Written as a scan rather than a regex because a regex
 * carrying literal control characters is unreadable.
 */
function withoutControls(value: string): string {
  let out = '';
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    out += code < 0x20 || code === 0x7f ? ' ' : ch;
  }
  return out;
}

/**
 * The name the `.eml` is saved under.
 *
 * Derived here rather than read from the response: `requestBlob` returns only
 * the body, so the backend's own `Content-Disposition` never reaches this
 * side. It is laundered the same way the backend launders it — a subject is
 * sender-controlled text, and a download called `../../x` or `CON` is a
 * problem on the way to disk, not on the wire.
 */
export function emlFilename(message: Pick<MessageSummary, 'subject'>): string {
  const cleaned = withoutControls(message.subject)
    .replace(UNSAFE_FILENAME, '-')
    .replace(/\s+/g, ' ')
    .trim();
  const base = cleaned.replace(/^\.+/, '').slice(0, 120).trim();
  return `${base === '' ? 'message' : base}.eml`;
}

export interface MessageDownload {
  /** Fetches the raw message and hands it to the browser as a file. */
  download: (message: MessageSummary) => Promise<void>;
  /** Id of the message currently downloading, for a busy affordance. */
  busyId: string | undefined;
  /** A written sentence when the last download failed. */
  error: string | undefined;
}

/**
 * Downloads a message as a `.eml` through the shared transport.
 *
 * The session is bound (ADR 0019), so the browser cannot fetch this itself:
 * an `<a download>` is a navigation and carries no proof header. The bytes
 * therefore come back through `api.messages.rawBlob` like every other
 * authenticated request and reach the user as a `blob:` URL, exactly as the
 * message frame's own subresources do.
 *
 * The object URL is not revoked on the spot — revoking while the download is
 * still being handed over cancels it in some browsers — so the registry holds
 * it until this hook unmounts.
 */
export function useMessageDownload(accountId: string | undefined): MessageDownload {
  const api = useApi();
  const registryRef = useRef<ObjectUrlRegistry | null>(null);
  const [busyId, setBusyId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(
    () => () => {
      registryRef.current?.revokeAll();
      registryRef.current = null;
    },
    [],
  );

  const download = useCallback(
    async (message: MessageSummary): Promise<void> => {
      if (accountId === undefined) return;
      setError(undefined);
      setBusyId(message.id);
      try {
        const blob = await api.messages.rawBlob(accountId, message.id);
        registryRef.current ??= new ObjectUrlRegistry();
        triggerDownload(registryRef.current.create(blob), emlFilename(message));
      } catch (cause) {
        setError(downloadFailureCopy(cause));
      } finally {
        setBusyId(undefined);
      }
    },
    [accountId, api],
  );

  return { download, busyId, error };
}

/** What to say when a download fails. Server text never reaches the user. */
function downloadFailureCopy(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isOffline) return 'Couldn’t download this message: the server is unreachable.';
    if (cause.status === 404) return 'This message is no longer in this folder.';
    if (cause.status === 413) return 'This message is too large to download through the web client.';
    return cause.message;
  }
  return 'Couldn’t download this message.';
}

export interface CopyVariables {
  messages: MessageSummary[];
  targetFolderId: string;
  /** The mailbox, when not the hook's: a Ctrl-drop copies into the row's own (ADR 0108). */
  accountId?: string;
}

/**
 * Copies messages into another folder.
 *
 * No optimistic patch: the copy lands in a folder whose list this client may
 * not be holding, with an id only the server can mint (`new_id`, and only
 * where the host has UIDPLUS). Invalidating on settle repaints the destination
 * the next time it is looked at, which is honest; a guessed row would not be.
 */
export function useCopyMessages(accountId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  const id = accountId ?? '';

  return useMutation<BulkResult, Error, CopyVariables>({
    scope: { id: `messages:${id}` },
    mutationFn: ({ messages, targetFolderId, accountId: account }) =>
      api.messages.copy(account ?? id, {
        messageIds: messages.map((message) => message.id),
        targetFolderId,
      }),
    onSettled: async (_data, _error, { accountId: account }) => {
      const target = account ?? id;
      await queryClient.invalidateQueries({ queryKey: queryKeys.account(target).messagesScope() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.account(target).folders() });
    },
  });
}
